import { authHeaders, ApiError } from './iotApi';
import type { PlanData } from './clientOnboardingApi';
import type { ClientOnboardingDraft } from '../types/clientOnboarding';

export interface CreationReceipt {
  apply_id: string;
  plan_digest: string;
  status: string;
  phase?: string;
  confirmation_expires_at?: string;
  confirmation_expired?: boolean;
  verification_status?: string;
  verification_digest?: string;
  plan_id?: string;
}

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

async function request<T>(path: string, body?: unknown): Promise<T> {
  const headers = authHeaders();
  let response: Response;
  try {
    response = await fetch(`${BASE}/api/v1/admin/client-onboarding/${path}`, {
      method: body === undefined ? 'GET' : 'POST', headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError('network_error', 'Client creation response unavailable');
  }
  let envelope;
  try { envelope = await response.json(); } catch {
    if (response.ok) throw new ApiError('invalid_response', 'Invalid client creation response');
  }
  if (!response.ok) {
    const codes: Record<number, string> = { 401: 'unauthenticated', 403: 'forbidden', 404: 'creation_unavailable',
      409: 'onboarding_conflict', 422: 'onboarding_invalid', 503: 'onboarding_unavailable' };
    let code = 'creation_unavailable';
    if (codes[response.status]) code = codes[response.status];
    if (response.status !== 401 && response.status !== 403
      && envelope?.success === false && envelope.data === null && typeof envelope?.error_code === 'string'
      && (envelope.error_code !== 'onboarding_not_found' || response.status === 404) && /^onboarding_[a-z_]+$/.test(envelope.error_code)) {
      code = envelope.error_code;
    }
    throw new ApiError(code, 'Client creation unavailable', response.status);
  }
  if (envelope?.success !== true || !envelope.data) {
    throw new ApiError('invalid_response', 'Invalid client creation response');
  }
  return envelope.data as T;
}

function receipt(value: CreationReceipt, id: string, operation: 'confirm' | 'inspect' | 'apply' | 'verify', expectedDigest?: string): CreationReceipt {
  if ((expectedDigest && value?.plan_digest !== expectedDigest) || !value || typeof value.plan_digest !== 'string' || value.apply_id !== id || !/^apply-[a-f0-9]{24}$/.test(value.apply_id) || !/^sha256:[a-f0-9]{64}$/.test(value.plan_digest)
    || value.apply_id !== `apply-${value.plan_digest.slice(7, 31)}`
    || !['CONFIRMED', 'APPLYING', 'APPLIED', 'VERIFIED', 'STALE', 'FAILED_RETRYABLE', 'FAILED_CONFLICT'].includes(value.status)) throw new ApiError('invalid_response', 'Invalid creation receipt');
  if (operation === 'confirm' && value.status !== 'CONFIRMED') throw new ApiError('invalid_response', 'Confirmation missing');
  if (['apply', 'inspect'].includes(operation)
    && !['PRECHECK', 'DOMAIN_STAGE', 'IDENTITY', 'GRANTS', 'FINALIZE', 'POST_VERIFY'].includes(value.phase || '')) {
    throw new ApiError('invalid_response', 'Execution phase missing');
  }
  if (operation === 'verify' && (value.status !== 'VERIFIED' || value.verification_status !== 'VERIFIED'
    || !/^sha256:[a-f0-9]{64}$/.test(value.verification_digest || ''))) throw new ApiError('invalid_response', 'Verification evidence missing');
  return value;
}

export const clientCreationApi = {
  capabilities: () => request<{ apply_enabled: boolean; max_farms: number }>('capabilities'),
  confirm: async (draft: ClientOnboardingDraft, plan: PlanData) => receipt(await request<CreationReceipt>('confirm', {
    schema_version: 1, mode: 'CONFIRM', draft, expected_plan_id: plan.plan_id,
    expected_plan_digest: plan.plan_digest, acknowledged_warning_codes: plan.warnings.map(w => w.code),
    confirmation: { intent: 'CREATE_CLIENT', accepted: true },
  }), `apply-${plan.plan_digest.slice(7, 31)}`, 'confirm', plan.plan_digest),
  inspect: async (id: string) => receipt(await request<CreationReceipt>(`executions/${encodeURIComponent(id)}`), id, 'inspect'),
  apply: async (item: CreationReceipt) => receipt(await request<CreationReceipt>('apply', {
    schema_version: 1, mode: 'APPLY_CONFIRMED_PLAN', apply_id: item.apply_id, expected_plan_digest: item.plan_digest,
  }), item.apply_id, 'apply', item.plan_digest),
  verify: async (item: CreationReceipt) => receipt(await request<CreationReceipt>('verify', {
    schema_version: 1, mode: 'VERIFY', apply_id: item.apply_id,
  }), item.apply_id, 'verify', item.plan_digest),
};
